import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, StatsCard, Table, Button, useToast } from '../components/common';
import api from '../services/api';
import type { DashboardStats, RequestLog, User } from '../types';

// 轉換為台北時間 (UTC+8)
const toTaipeiTime = (dateStr: string) => {
  const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  const date = new Date(utcDateStr);
  return date.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', hour12: false });
};

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatTokens = (tokens: number) => {
  if (tokens >= 1000000) return (tokens / 1000000).toFixed(2) + 'M';
  if (tokens >= 1000) return (tokens / 1000).toFixed(1) + 'K';
  return tokens.toString();
};

// 計算 Token 成本：每百萬 Token $0.05
const calculateTokenCost = (tokens: number) => {
  const cost = (tokens / 1000000) * 0.05;
  if (cost < 0.01) return '< $0.01';
  return `$${cost.toFixed(2)}`;
};

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

// Get date N days ago in YYYY-MM-DD format
const getDateDaysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
};

type DatePreset = 'today' | 'week' | 'month' | 'all' | 'custom';

export default function DashboardPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  
  // Filter states
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  const isAdmin = user?.is_superuser;

  useEffect(() => {
    if (isAdmin) {
      // Fetch users list for admin filter
      api.get<User[]>('/api/v1/users').then(res => {
        setUsers(res.data);
      }).catch(() => {});
    }
    fetchDashboardStats();
  }, [isAdmin]);

  const fetchDashboardStats = async (userId?: string, start?: string, end?: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (userId) params.append('user_id', userId);
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      
      const queryString = params.toString();
      const url = `/api/v1/dashboard${queryString ? `?${queryString}` : ''}`;
      const response = await api.get<DashboardStats>(url);
      setStats(response.data);
    } catch (error) {
      addToast('error', '無法載入儀表板資料');
    } finally {
      setLoading(false);
    }
  };

  const handleDatePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    let start = '';
    let end = '';
    
    switch (preset) {
      case 'today':
        start = getTodayDate();
        end = getTodayDate();
        break;
      case 'week':
        start = getDateDaysAgo(7);
        end = getTodayDate();
        break;
      case 'month':
        start = getDateDaysAgo(30);
        end = getTodayDate();
        break;
      case 'all':
        start = '';
        end = '';
        break;
      case 'custom':
        // Keep current custom dates
        return;
    }
    
    setStartDate(start);
    setEndDate(end);
    fetchDashboardStats(selectedUserId, start, end);
  };

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
    fetchDashboardStats(userId, startDate, endDate);
  };

  const handleCustomDateApply = () => {
    fetchDashboardStats(selectedUserId, startDate, endDate);
  };

  const handleClearFilters = () => {
    setSelectedUserId('');
    setDatePreset('all');
    setStartDate('');
    setEndDate('');
    fetchDashboardStats();
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        無法載入資料
      </div>
    );
  }

  const logColumns = [
    { key: 'created_at', header: '時間', render: (log: RequestLog) => toTaipeiTime(log.created_at) },
    ...(isAdmin ? [{ 
      key: 'user_username', 
      header: '用戶', 
      render: (log: RequestLog) => log.user_username || '-' 
    }] : []),
    { key: 'target_url', header: '目標網址', render: (log: RequestLog) => (
      <span className="truncate max-w-xs block" title={log.target_url}>{log.target_url}</span>
    )},
    { key: 'status_code', header: '狀態', render: (log: RequestLog) => (
      <span className={`px-2 py-1 rounded text-xs ${
        log.status_code && log.status_code < 400 
          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      }`}>
        {log.status_code || '錯誤'}
      </span>
    )},
    { key: 'duration_ms', header: '耗時', render: (log: RequestLog) => log.duration_ms ? `${log.duration_ms}ms` : '-' },
    { key: 'tokens_used', header: 'Tokens', render: (log: RequestLog) => log.tokens_used ? formatTokens(log.tokens_used) : '-' },
    { key: 'traffic_bytes', header: '流量', render: (log: RequestLog) => log.traffic_bytes ? formatBytes(log.traffic_bytes) : '-' },
  ];

  const hasFilters = selectedUserId || startDate || endDate;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">儀表板</h1>
      
      {/* Filters - Admin only for user filter */}
      <Card>
        <div className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            {isAdmin && (
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">用戶篩選</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => handleUserChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">全部用戶</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="w-36">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">日期範圍</label>
              <select
                value={datePreset}
                onChange={(e) => handleDatePresetChange(e.target.value as DatePreset)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">全部時間</option>
                <option value="today">今天</option>
                <option value="week">最近 7 天</option>
                <option value="month">最近 30 天</option>
                <option value="custom">自訂</option>
              </select>
            </div>
            {datePreset === 'custom' && (
              <>
                <div className="w-40">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">開始日期</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="w-40">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">結束日期</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <Button size="sm" onClick={handleCustomDateApply}>套用</Button>
              </>
            )}
            {hasFilters && (
              <Button variant="secondary" size="sm" onClick={handleClearFilters}>清除篩選</Button>
            )}
          </div>
          {loading && (
            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">載入中...</div>
          )}
        </div>
      </Card>
      
      {/* Stats Cards - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="今日請求" value={stats.total_requests_today} icon="📊" />
        <StatsCard title="本週請求" value={stats.total_requests_week} icon="📈" />
        <StatsCard title="本月請求" value={stats.total_requests_month} icon="📉" />
        <StatsCard title="成功率" value={`${stats.success_rate.toFixed(1)}%`} icon="✅" />
      </div>

      {/* Stats Cards - Row 2: Token & Traffic */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard 
          title="總 Token 使用量" 
          value={formatTokens(stats.total_tokens)} 
          icon="🔤" 
          subtitle={`預估成本：${calculateTokenCost(stats.total_tokens)}`}
        />
        <StatsCard title="總流量" value={formatBytes(stats.total_traffic_bytes)} icon="📡" />
        {/* Only show global stats when no filters applied */}
        {isAdmin && !hasFilters && (
          <>
            <StatsCard title="總用戶數" value={stats.total_users || 0} icon="👥" />
            <StatsCard title="啟用 API Key" value={stats.active_api_keys || 0} icon="🔑" />
          </>
        )}
      </div>

      {/* Recent Logs */}
      <Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            最近請求紀錄
            {hasFilters && <span className="text-sm font-normal text-gray-500 ml-2">（已篩選）</span>}
          </h2>
        </div>
        <Table
          data={stats.recent_logs}
          columns={logColumns}
          keyExtractor={(log) => log.id}
          emptyMessage="暫無請求紀錄"
        />
      </Card>
    </div>
  );
}
